import React, {
  HTMLAttributes,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Element, Navbar } from 'react-bulma-components';
import { RenderAsComponent } from 'react-bulma-components/src/components';
import If from '../../shared/if/if';
import AnimatedChevronIcon from '../animated-chevron-icon/animated-chevron-icon';
import styles from './dropdown-menu.module.scss';

export interface DropdownMenuProps {
  children: ReactNode;
  label: ReactNode;
  showArrow?: boolean;
  right?: boolean;
}

type DropdownMenuContextType = {
  close: () => void;
};

const DropdownMenuContext = createContext<DropdownMenuContextType>({
  close: () => {},
});

export function DropdownMenu({
  children,
  label: _label,
  showArrow = true,
  right = true,
}: DropdownMenuProps) {
  const [active, setActive] = useState(false);
  const dropdownRef = useRef<RenderAsComponent>(null);

  const handleDropdownOnClick: React.MouseEventHandler<HTMLAnchorElement> =
    useCallback((e) => {
      e.stopPropagation();
      setActive((prev) => !prev);
    }, []);

  const handleDropdownOnKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (
        e.key === 'Enter' ||
        e.key === ' ' ||
        (e.key === 'Escape' && active)
      ) {
        handleDropdownOnClick(
          e as unknown as React.MouseEvent<HTMLAnchorElement>
        );
      }
    },
    [active, handleDropdownOnClick]
  );

  const close = useCallback(() => {
    setActive(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !(dropdownRef.current as unknown as HTMLElement).contains(
          event.target as Node
        )
      ) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [close]);

  const label = useMemo(() => {
    if (showArrow) {
      return (
        <Element display="flex" alignItems="center" className="gap-2">
          {_label}
          <AnimatedChevronIcon
            className="has-text-primary-dark"
            active={active}
          />
        </Element>
      );
    }
    return _label;
  }, [_label, active, showArrow]);

  const labelClassName = useMemo(() => {
    let className = styles.label;

    if (active) {
      className += ` ${styles.active}`;
    }

    return className;
  }, [active]);

  return (
    <DropdownMenuContext.Provider value={{ close }}>
      <Navbar.Item
        active={active}
        onClick={handleDropdownOnClick}
        onKeyDown={handleDropdownOnKeyDown}
        className={styles.container}
        domRef={dropdownRef}
        tabIndex={0}
      >
        <Navbar.Link paddingless arrowless className={labelClassName}>
          {label}
        </Navbar.Link>
        <Navbar.Dropdown className="is-borderless" right={right}>
          {children}
        </Navbar.Dropdown>
      </Navbar.Item>
    </DropdownMenuContext.Provider>
  );
}

type ItemProps = HTMLAttributes<HTMLAnchorElement> & {
  closeOnClick?: boolean;
  stopEventPropagationOnClick?: boolean;
  children: ReactNode;
  show?: boolean;
};

const Item = ({
  onClick,
  closeOnClick = true,
  stopEventPropagationOnClick = true,
  children,
  show = true,
}: ItemProps) => {
  const { close } = useContext(DropdownMenuContext);

  const handleOnClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    if (stopEventPropagationOnClick) {
      e.stopPropagation();
    }

    onClick?.(e);

    if (closeOnClick) {
      close();
    }
  };

  return (
    <If condition={show}>
      <Navbar.Item onClick={handleOnClick} tabIndex={1}>
        {children}
      </Navbar.Item>
    </If>
  );
};

DropdownMenu.Item = Item;

export default DropdownMenu;
